import { t } from "i18next";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useFetch } from "../../hooks/useFetch";
import { useAppStore } from "../../stores/RootStore";
import { Equipment } from "../../types/types";
import { Separator } from "../../ui/Separator";
import Spinner from "../../ui/Spinner/Spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/Tabs/Tabs";
import { CalculationsTab } from "./tabs/calculationsTab/CalculationsTab";
import ChangeLogTab from "./tabs/changelogTab/ChangeLogTab";
import DevicesTab from "./tabs/devicesTab/DevicesTab";
import MainTab from "./tabs/mainTab/MainTab";
import MaintenancesTab from "./tabs/maintenancesTab/MaintenancesTab";
import { Notifications } from "./tabs/notificationsTab/Notifications";
import RelatedTab from "./tabs/relatedTab/RelatedTab";
import { ReportTab } from "./tabs/reportTab/ReportTab";
import { Triggers } from "./tabs/triggersTab/Triggers";

const EquipmentEdit = () => {
  const { id } = useParams();
  const store = useAppStore();
  const methods = useForm();
  const { reset, getValues } = methods;

  const [loading, setLoading] = useState<boolean>(true);
  const [currentEquipment, setCurrentEquipment] = useState<Equipment | null>(null);

  const [{ response, isLoading, error }, doFetchItem] = useFetch(`/directories/equipment_items/${id}`);
  const [{ response: sensorsResponse, isLoading: sensorsLoading, error: sensorsError }, doFetchItemSensors] = useFetch(`/directories/equipment_items/${id}/sensors`);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    doFetchItem();
    doFetchItemSensors();
  }, [id]);

  useEffect(() => {
    if (!response || !sensorsResponse) {
      return;
    }
    let equipment = response?.data as unknown as Equipment;
    let sensors = sensorsResponse?.data
    reset({
      equipment_name: equipment.attributes.name,
      inventory_number: equipment.attributes.inventory_number,
      factory_number: equipment.attributes.factory_number,
      producer_id: equipment.attributes.producer_id,
      equipment_model_id: equipment.attributes.equipment_model_id,
      stop_threshold: equipment.attributes.stop_threshold,
      balance_date: equipment.attributes.balance_date ? new Date(equipment.attributes.balance_date) : null,
      release_date: equipment.attributes.release_date ? new Date(equipment.attributes.release_date) : null,
      status: equipment.attributes.status,
      move_status: equipment.attributes.move_status,
      division_id: equipment.attributes.division_id,
      responsible_id: equipment.attributes.responsible_id,
      parent_id: equipment.attributes.parent_id,
    });
    setCurrentEquipment({...response.data, sensors: sensors} as unknown as Equipment);
  }, [response, sensorsResponse]);

  useEffect(() => {
    if (!store.equipments?.length || !store.equipmentModels?.length || !currentEquipment) {
      return;
    }
    setLoading(false);
  }, [store.equipments, store.equipmentModels, currentEquipment]);

  return loading ? (
    <Spinner />
  ) : (
    <>
      <Tabs defaultValue={currentEquipment?.attributes.with_sensors ? "report" : "main"} className="flex flex-col bg-background relative h-full w-full overflow-hidden">
        <TabsList className="flex-none h-12 w-full px-6 justify-start">
          { currentEquipment?.attributes.with_sensors && <TabsTrigger className="data-[state=active]:bg-primary data-[state=active]:text-white" value="report">
            {t("reports")}
          </TabsTrigger> }
          <TabsTrigger className="data-[state=active]:bg-primary data-[state=active]:text-white" value="main">
            {t("main_info")}
          </TabsTrigger>
          <TabsTrigger className="data-[state=active]:bg-primary data-[state=active]:text-white" value="device">
            {t("device")}
          </TabsTrigger>
          <TabsTrigger className="data-[state=active]:bg-primary data-[state=active]:text-white" value="equipment_calculations">
            {t("equipment_calculations")}
          </TabsTrigger>
          <TabsTrigger className="data-[state=active]:bg-primary data-[state=active]:text-white" value="maintenances">
            {t("maintenances")}
          </TabsTrigger>
          <TabsTrigger className="data-[state=active]:bg-primary data-[state=active]:text-white" value="triggers">
            {t("triggers")}
          </TabsTrigger>
          <TabsTrigger className="data-[state=active]:bg-primary data-[state=active]:text-white" value="notifications">
            {t("notifications")}
          </TabsTrigger>
          <TabsTrigger className="data-[state=active]:bg-primary data-[state=active]:text-white" value="related">
            {t("related")}
          </TabsTrigger>
          <TabsTrigger className="data-[state=active]:bg-primary data-[state=active]:text-white" value="changelog">
            {t("changelog")}
          </TabsTrigger>
        </TabsList>
        <Separator />
        <div className="flex flex-col w-full h-full justify-start relative overflow-hidden">
          <TabsContent className="flex flex-col min-w-[1008px] w-full data-[state=active]:h-full m-0" value="report"><ReportTab equipment={currentEquipment!}/></TabsContent>
          <TabsContent className="flex flex-col min-w-[808px] w-full data-[state=active]:h-full m-0" value="main">
            {currentEquipment && (
              <FormProvider {...methods}>
                <MainTab currentEquipment={currentEquipment} setCurrentEquipment={setCurrentEquipment} />
              </FormProvider>
            )}
          </TabsContent>
          <TabsContent className="flex flex-col w-full min-w-[808px] data-[state=active]:h-full m-0" value="device">
            <DevicesTab id={id!} />
          </TabsContent>
          <TabsContent className="flex flex-col w-full min-w-[808px] data-[state=active]:h-full m-0" value="equipment_calculations">
            <CalculationsTab equipment={currentEquipment!} updateEquipment={setCurrentEquipment} />
          </TabsContent>
          <TabsContent className="flex  w-full data-[state=active]:h-full m-0" value="maintenances">
            <MaintenancesTab id={id!}/>
          </TabsContent>
          <TabsContent className="flex flex-col min-w-[808px] w-full data-[state=active]:h-full m-0" value="triggers">
            <Triggers equipment={currentEquipment!}/>
          </TabsContent>
          <TabsContent className="flex flex-col min-w-[808px] w-full data-[state=active]:h-full m-0" value="notifications">
            <Notifications equipment={currentEquipment!}/>
          </TabsContent>
          <TabsContent className="flex flex-col min-w-[808px] w-full data-[state=active]:h-full m-0" value="related">
            <RelatedTab currentEquipment={currentEquipment!}/>
          </TabsContent>
          <TabsContent className="flex flex-col min-w-[808px] w-full data-[state=active]:h-full m-0" value="changelog">
            <ChangeLogTab id={id!}/>
          </TabsContent>
        </div>
      </Tabs>
    </>
  );
};

export default EquipmentEdit;
